import React, { useState } from 'react';
import axios from 'axios';
import '../styles/Transcript.css';

interface TranscriptSegment {
  text: string;
  start?: number;
  end?: number;
}

interface VideoStats {
  views?: number;
  likes?: number;
  comments?: number;
  shares?: number;
  platform?: string;
}

interface TranscriptData {
  originalTranscript?: string;
  translatedTranscript?: string;
  segments?: TranscriptSegment[];
  stats?: VideoStats;
  analysis?: {
    original: {
      hook: string;
      value: string;
      cta: string;
    };
    translated: {
      hook: string;
      value: string;
      cta: string;
    };
  };
  raw?: any; // For debugging
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const Transcript: React.FC = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transcriptData, setTranscriptData] = useState<TranscriptData | null>(null);
  const [activeTab, setActiveTab] = useState<'transcript' | 'analysis' | 'debug'>('transcript');
  const [debugInfo, setDebugInfo] = useState<any>(null);

  const detectPlatform = (url: string): string | null => {
    if (url.includes('tiktok.com')) return 'TikTok';
    if (url.includes('instagram.com')) return 'Instagram';
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'YouTube';
    return null;
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!url.trim()) {
      setError('Please enter a URL.');
      return;
    }

    const platform = detectPlatform(url);
    if (!platform) {
      setError('Please enter a valid TikTok, Instagram, or YouTube URL.');
      return;
    }

    setLoading(true);
    setError(null);
    setTranscriptData(null);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/transcript`, {
        params: {
          url: url.trim(),
          text: 'true',
          mode: 'auto'
        }
      });

      console.log('API Response:', response.data);

      // Store raw response for debugging
      setDebugInfo({
        status: response.status,
        data: response.data,
        timestamp: new Date().toISOString()
      });

      // Handle different response formats
      const originalTranscript = response.data.originalTranscript || '';
      const translatedTranscript = response.data.translatedTranscript || '';
      const segments = response.data.segments || [];
      const stats = response.data.stats || null;
      const analysis = response.data.analysis || null;

      if (!originalTranscript && !translatedTranscript && (!segments || segments.length === 0)) {
        setError('No transcript data received. Check the Debug tab for details.');
        setActiveTab('debug');
        return;
      }

      setTranscriptData({
        originalTranscript,
        translatedTranscript,
        segments,
        stats,
        analysis,
        raw: response.data
      });

      setActiveTab('transcript');
    } catch (err: any) {
      console.error('Error fetching transcript:', err);
      const errorDetails = err.response?.data || err.message;
      setDebugInfo({
        error: true,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: errorDetails,
        message: err.message,
        timestamp: new Date().toISOString()
      });
      setError(
        `Failed to fetch transcript: ${err.response?.data?.error || err.message}. Check Debug tab for details.`
      );
      setActiveTab('debug');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds?: number): string => {
    if (!seconds && seconds !== 0) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatNumber = (num?: number): string => {
    if (!num && num !== 0) return 'N/A';
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  return (
    <div className="transcript-container">
      <div className="transcript-header">
        <h1>Video Transcript Analysis</h1>
        <p className="transcript-subtitle">
          Enter a TikTok, Instagram, or YouTube URL to view the full transcript and Hook-Value-CTA analysis
        </p>
      </div>

      <form onSubmit={handleSubmit} className="transcript-form">
        <div className="input-group">
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Enter video URL (TikTok, Instagram, YouTube)"
            className="url-input"
            disabled={loading}
          />
          <button
            type="submit"
            className="submit-button"
            disabled={loading}
          >
            {loading ? 'Analyzing...' : 'Analyze'}
          </button>
        </div>
        {error && <div className="error-message">{error}</div>}
      </form>

      {(transcriptData || debugInfo) && (
        <div className="transcript-results">
          {transcriptData?.stats && (
            <div className="video-stats">
              <h3>Video Statistics</h3>
              <div className="stats-grid">
                {transcriptData.stats.views !== undefined && (
                  <div className="stat-item">
                    <div className="stat-icon">👁️</div>
                    <div className="stat-label">Views</div>
                    <div className="stat-value">{formatNumber(transcriptData.stats.views)}</div>
                  </div>
                )}
                {transcriptData.stats.likes !== undefined && (
                  <div className="stat-item">
                    <div className="stat-icon">❤️</div>
                    <div className="stat-label">Likes</div>
                    <div className="stat-value">{formatNumber(transcriptData.stats.likes)}</div>
                  </div>
                )}
                {transcriptData.stats.comments !== undefined && (
                  <div className="stat-item">
                    <div className="stat-icon">💬</div>
                    <div className="stat-label">Comments</div>
                    <div className="stat-value">{formatNumber(transcriptData.stats.comments)}</div>
                  </div>
                )}
                {transcriptData.stats.shares !== undefined && (
                  <div className="stat-item">
                    <div className="stat-icon">🔄</div>
                    <div className="stat-label">Shares</div>
                    <div className="stat-value">{formatNumber(transcriptData.stats.shares)}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="tabs">
            <button
              className={`tab ${activeTab === 'transcript' ? 'active' : ''}`}
              onClick={() => setActiveTab('transcript')}
              disabled={!transcriptData}
            >
              Full Transcript
            </button>
            <button
              className={`tab ${activeTab === 'analysis' ? 'active' : ''}`}
              onClick={() => setActiveTab('analysis')}
              disabled={!transcriptData}
            >
              Hook-Value-CTA Analysis
            </button>
            <button
              className={`tab ${activeTab === 'debug' ? 'active' : ''}`}
              onClick={() => setActiveTab('debug')}
            >
              Debug Info
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'transcript' && transcriptData && (
              <div className="transcript-content">
                <h2>Full Transcript</h2>
                <div className="transcript-dual-column">
                  <div className="transcript-column">
                    <h3 className="column-header">Original</h3>
                    <div className="transcript-text">
                      {transcriptData.originalTranscript || 'No original transcript available'}
                    </div>
                  </div>
                  <div className="transcript-column">
                    <h3 className="column-header">한국어 번역</h3>
                    <div className="transcript-text">
                      {transcriptData.translatedTranscript || 'No translation available'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'analysis' && transcriptData && transcriptData.analysis && (
              <div className="analysis-content">
                <h2>Hook-Value-CTA 구조 분석</h2>

                <div className="analysis-section hook">
                  <h3>🪝 Hook (훅)</h3>
                  <p className="section-description">
                    시청자의 관심을 끄는 오프닝
                  </p>
                  <div className="analysis-dual-column">
                    <div className="analysis-column">
                      <h4 className="analysis-column-header">Original</h4>
                      <div className="analysis-text">
                        {transcriptData.analysis.original.hook}
                      </div>
                    </div>
                    <div className="analysis-column">
                      <h4 className="analysis-column-header">한국어</h4>
                      <div className="analysis-text">
                        {transcriptData.analysis.translated.hook}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="analysis-section value">
                  <h3>💎 Value (가치)</h3>
                  <p className="section-description">
                    핵심 메시지와 가치 제안
                  </p>
                  <div className="analysis-dual-column">
                    <div className="analysis-column">
                      <h4 className="analysis-column-header">Original</h4>
                      <div className="analysis-text">
                        {transcriptData.analysis.original.value}
                      </div>
                    </div>
                    <div className="analysis-column">
                      <h4 className="analysis-column-header">한국어</h4>
                      <div className="analysis-text">
                        {transcriptData.analysis.translated.value}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="analysis-section cta">
                  <h3>📢 CTA (행동 유도)</h3>
                  <p className="section-description">
                    시청자의 행동을 유도하는 클로징
                  </p>
                  <div className="analysis-dual-column">
                    <div className="analysis-column">
                      <h4 className="analysis-column-header">Original</h4>
                      <div className="analysis-text">
                        {transcriptData.analysis.original.cta}
                      </div>
                    </div>
                    <div className="analysis-column">
                      <h4 className="analysis-column-header">한국어</h4>
                      <div className="analysis-text">
                        {transcriptData.analysis.translated.cta}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'debug' && debugInfo && (
              <div className="debug-content">
                <h2>Debug Information</h2>
                <div className="debug-section">
                  <h3>Request Details</h3>
                  <p><strong>Timestamp:</strong> {debugInfo.timestamp}</p>
                  <p><strong>Status:</strong> {debugInfo.status || 'N/A'}</p>
                  {debugInfo.statusText && <p><strong>Status Text:</strong> {debugInfo.statusText}</p>}
                  {debugInfo.message && <p><strong>Error Message:</strong> {debugInfo.message}</p>}
                </div>

                <div className="debug-section">
                  <h3>Server Response</h3>
                  <pre className="debug-json">
                    {JSON.stringify(debugInfo.data, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Transcript;
